import React, { useState, useEffect } from 'react';
import { Modal, Button, List, Checkbox, Space, message, Spin, Empty, Card, Skeleton, Progress } from 'antd';
import { InboxOutlined, DeleteOutlined } from '@ant-design/icons';
import type { UploadProps } from 'antd/es/upload/interface';
import { RcFile } from 'antd/es/upload';
import { UploadRequestOption } from 'rc-upload/lib/interface';
import Dragger from 'antd/lib/upload/Dragger';
import { SessionFile, useChatApi } from 'api/useChat';

interface FileUploadDialogProps {
  visible: boolean;
  projectId?: string;
  onCancel: () => void;
  onOk: (selectedFiles: SessionFile[]) => void;
  source: string;
}

const FileUploadDialog: React.FC<FileUploadDialogProps> = ({ visible, onCancel, onOk, projectId }) => {
  // State for uploaded files
  const [uploadingFiles, setUploadingFiles] = useState<RcFile[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<SessionFile[]>([]);
  const [salesforceFiles, setSalesforceFiles] = useState<SessionFile[]>([]);

  const [loadingFiles, setLoadingFiles] = useState(false);
  const [stagingFiles, setStagingFiles] = useState(false);
  const [deletingFile, setDeletingFile] = useState<string | null>(null);

  // State for selected files
  const [selectedFiles, setSelectedFiles] = useState<SessionFile[]>([]);

  // API hooks
  const { uploadFileToSession, stageFilesForSession, getAllFilesBySessionId, deleteFileFromSession } = useChatApi();

  // Load Salesforce files when modal opens
  useEffect(() => {
    if (visible) {
      loadFiles();
      setSelectedFiles([]);
    }
  }, [visible]);

  const loadFiles = async () => {
    if (!projectId) return;

    setLoadingFiles(true);
    try {
      const files = await getAllFilesBySessionId(projectId);
      setSalesforceFiles(files.filter((file) => file.source === 'salesforce'));
      setUploadedFiles(files.filter((file) => file.source === 'user'));
      setLoadingFiles(false);
    } catch (error) {
      message.error('Failed to load Salesforce files');
      console.error('Error loading Salesforce files:', error);
      setLoadingFiles(false);
    }
  };

  // Handle file upload
  const handleUpload = async (options: UploadRequestOption) => {
    if (!projectId) return;
    try {
      const result: SessionFile[] = await uploadFileToSession(options, projectId);
      if (result) {
        setUploadedFiles((prev) => [...result, ...prev]);
        message.success(`${options.filename} uploaded successfully`);
      }
    } catch (error) {
      message.error(`Failed to upload ${options.filename}`);
      console.error('Upload error:', error);
    }
  };

  // Upload props for drag and drop
  const uploadProps: UploadProps = {
    multiple: true,
    accept: '.pdf,.docx,.csv,.xlsx,.pptx,.txt,.rtf,.odt.jpg,.jpeg,.png,.avif,.tiff,.heic,.heif,.bmp,.webp',
    fileList: uploadingFiles,
    maxCount: 5,
    customRequest: (options: UploadRequestOption) => {
      handleUpload(options);
      return false;
    }, //custom request to handle upload
    onChange: (info) => {
      if (info.file.status === 'done') {
        //remove the file from the uploading files
        setUploadingFiles((prev) => prev.filter((f) => f.uid !== info.file.uid));
      } else {
        setUploadingFiles(info.fileList as RcFile[]);
      }
    } //set uploading files
  };

  // Handle uploaded file selection
  const handleFileSelect = (file: SessionFile, checked: boolean) => {
    if (checked) {
      if (file) {
        setSelectedFiles((prev) => [...prev, file]);
      }
    } else {
      setSelectedFiles((prev) => prev.filter((f) => f.id !== file.id));
    }
  };

  const handleFileDelete = async (fileId: string) => {
    try {
      if (!projectId) return;
      setDeletingFile(fileId);
      await deleteFileFromSession(fileId, projectId);
      setUploadedFiles((prev) => prev.filter((f) => f.id !== fileId));
      setSelectedFiles((prev) => prev.filter((f) => f.id !== fileId));
    } catch (error) {
      message.error('Failed to delete file');
      console.error('Error deleting file:', error);
    } finally {
      setDeletingFile(null);
    }
  };

  // Handle modal OK
  const handleOk = async () => {
    if (!projectId) return;
    setStagingFiles(true);
    try {
      const { success, failed } = await stageFilesForSession(projectId, selectedFiles);
      if (failed.length > 0) {
        const failedFileNames = failed.map((file) => file.filename).join(', ');
        message.error(`Error can not use these files: ${failedFileNames}`);
        console.error('Error staging files:', failed);
      }
      if (success.length > 0) {
        onOk(success);
      }
    } catch (error) {
      message.error('Failed to stage files');
      console.error('Error staging files:', error);
    } finally {
      setStagingFiles(false);
    }
  };

  // Handle modal cancel
  const handleCancel = () => {
    onCancel();
  };

  // Render uploaded files list
  const renderFiles = (files: SessionFile[], type: 'user' | 'salesforce') => (
    <Card title={type === 'user' ? 'Uploaded Files' : 'Salesforce Files'} size="small" className="w-full flex-1 ">
      {loadingFiles ? (
        <Skeleton active loading={loadingFiles} paragraph={{ rows: 5 }} />
      ) : files.length === 0 ? (
        <Empty description={`No files available`} />
      ) : (
        <List
          size="small"
          dataSource={files}
          className="max-h-[200px] overflow-y-auto"
          renderItem={(file) => (
            <List.Item
              actions={
                type === 'user'
                  ? [
                      <Button
                        key="remove"
                        type="text"
                        size="small"
                        danger={true}
                        loading={deletingFile === file.id}
                        icon={<DeleteOutlined />}
                        onClick={() => {
                          handleFileDelete(file.id);
                        }}
                      />
                    ]
                  : []
              }
            >
              <div className="flex items-center w-full">
                <Checkbox
                  checked={selectedFiles.some((f) => f.id === file.id)}
                  onChange={(e) => handleFileSelect(file, e.target.checked)}
                  className="mr-2"
                />
                <List.Item.Meta title={file.filename} />
              </div>
            </List.Item>
          )}
        />
      )}
    </Card>
  );

  return (
    <Modal
      title="Select Files"
      open={visible}
      onCancel={handleCancel}
      onOk={handleOk}
      width={1000}
      zIndex={999999999999}
      okText="Use Selected Files"
      cancelText="Cancel"
      destroyOnClose={true}
      okButtonProps={{ disabled: selectedFiles.length === 0, loading: stagingFiles }}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        {/* File Upload Section */}

        <Dragger {...uploadProps}>
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">Click or drag files to this area to upload</p>
          <p className="ant-upload-hint">
            Support for multiple file uploads. Supported formats: PDF, DOCX, CSV, XLSX, PPTX, TXT, RTF, ODT, Images
            (JPG, PNG, AVIF, TIFF, HEIC, HEIF, BMP, WEBP), and more.
          </p>
        </Dragger>

        <div className="flex  gap-2">
          {/* Uploaded Files */}
          {renderFiles(uploadedFiles, 'user')}

          {/* Salesforce Files */}
          {renderFiles(salesforceFiles, 'salesforce')}
        </div>
      </Space>
    </Modal>
  );
};

export default FileUploadDialog;
